// The SSE connection for answers, shared by every chat view.
//
// - A failed request keeps the server's { code, message } as an ApiError; the
//   stock adapter reduces it to "HTTP error! status: 422".
// - Loading a conversation's history is tracked per thread, so the page can
//   say "Loading conversation…" and show a clear error if it fails.

import { fetchServerSentEvents } from '@tanstack/ai-react'
import { useSyncExternalStore } from 'react'
import { ApiError, reportUnauthorized } from './api.js'

async function chatFetch(input, init) {
  let response
  try {
    response = await fetch(input, init)
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause
    throw new ApiError(0, 'network', 'You appear to be offline. Check your connection and try again.')
  }
  if (response.ok) return response
  const data = await response
    .clone()
    .json()
    .catch(() => null)
  const error = new ApiError(
    response.status,
    data?.error?.code ?? 'http_error',
    data?.error?.message ?? `Request failed (${response.status}).`,
  )
  if (response.status === 401) reportUnauthorized(error)
  throw error
}

// ---- History loading, per thread -------------------------------------------

const IDLE = Object.freeze({ status: 'idle' })
const history = new Map()
const listeners = new Set()

function setHistory(threadId, state) {
  history.set(threadId, state)
  // Hydration can start while a view renders; notify after it.
  queueMicrotask(() => listeners.forEach((fn) => fn()))
}

const subscribe = (fn) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

const base = fetchServerSentEvents('/api/chat', { fetchClient: chatFetch })

export const chatConnection = {
  ...base,
  async hydrate(threadId, options) {
    setHistory(threadId, { status: 'loading' })
    try {
      const result = await base.hydrate(threadId, options)
      setHistory(threadId, { status: 'done' })
      return result
    } catch (error) {
      setHistory(threadId, { status: 'error', error })
      throw error
    }
  },
}

/** { status: 'idle' | 'loading' | 'done' | 'error', error? } for a thread's history. */
export function useHistoryLoad(threadId) {
  return useSyncExternalStore(subscribe, () => history.get(threadId) ?? IDLE)
}
